/**
 * CLOUDWEB-21024: debug-утилиты в консоли Service Worker.
 *
 * Зачем отдельно от page-команд (`app.webPush.*` в webPush.console.ts): из консоли воркера
 * мы читаем состояние **этого** инстанса напрямую — без MessageChannel, таймаута и наличия
 * открытой вкладки. Это единственный способ посмотреть воркер, поднятый пушем, когда страниц
 * нет вообще; page-команде в этом случае просто не с кем разговаривать.
 *
 * Обратная сторона: консоль воркера доступна, только пока он running, и свежий инстанс,
 * поднятый под инспектор, читает конфиг из IDB — то есть показывает картину холодного старта.
 */
import { getSwRevision } from 'serviceWorker/build';
import { getSwOmicronConfigState, isPushDedupEnabled } from 'serviceWorker/omicronConfig';
import { clearShownPushIds } from 'serviceWorker/push/showedPushIds';

// В tsconfig подключены и DOM, и WebWorker, поэтому тип self в обычном модуле не сужен до
// ServiceWorkerGlobalScope. Приводим сами: расширять глобальный тип ради debug-утилиты незачем.
const swScope = self as unknown as ServiceWorkerGlobalScope;
const globalScope = self as unknown as Record<string, unknown>;

/**
 * Части состояния, которые живут в энтрипоинте воркера, — инжектим, чтобы этот модуль не
 * импортировал `firebase-messaging-sw` (иначе получился бы цикл). Тот же приём, что у
 * page-версии с `WebPushConsoleActions`.
 */
export interface SwConsoleActions {
    /**
     * Диплинк, который воркер удерживает прямо сейчас. Асинхронный: с CLOUDWEB-21080 рекорд
     * лежит в IDB, а не в памяти инстанса.
     */
    getPendingDeeplink: () => Promise<string | null>;
}

/** Каждый срез — со своей защитой: сбой одного не должен обрушить весь дамп. */
async function safe<T>(read: () => Promise<T>): Promise<T | { error: string }> {
    try {
        return await read();
    } catch (error) {
        return { error: String(error) };
    }
}

/**
 * Состояние воркера одним вызовом. Здесь собраны факты, видимые ТОЛЬКО из SW:
 * `permissionState` (в воркере нет `Notification.permission`), подписка и содержимое трея
 * при закрытых вкладках, удерживаемый диплинк.
 */
async function getSwStatus(actions: SwConsoleActions) {
    const [state, dedupEnabled, permission, subscription, notifications] = await Promise.all([
        safe(() => getSwOmicronConfigState()),
        safe(() => isPushDedupEnabled()),
        // Опции обязаны совпадать с теми, на которых оформлялась подписка, иначе часть
        // браузеров отвечает неожиданно.
        safe(() => swScope.registration.pushManager.permissionState({ userVisibleOnly: true })),
        safe(async () => {
            const sub = await swScope.registration.pushManager.getSubscription();

            if (!sub) {
                return null;
            }

            const json = sub.toJSON();

            return {
                endpoint: json.endpoint,
                hasKeys: Boolean(json.keys?.p256dh && json.keys?.auth),
                expirationTime: json.expirationTime,
            };
        }),
        // Прямая проверка склейки по tag: сколько уведомлений реально висит в трее.
        safe(async () => {
            const shown = await swScope.registration.getNotifications();

            return shown.map((notification) => ({
                tag: notification.tag,
                title: notification.title,
                id: (notification.data as Record<string, unknown> | null)?.id,
            }));
        }),
    ]);

    return {
        revision: getSwRevision(),
        scope: swScope.registration.scope,
        config: state,
        dedupEnabled,
        permission,
        subscription,
        notifications,
        pendingDeeplink: await safe(() => actions.getPendingDeeplink()),
    };
}

/**
 * Регистрирует `app.webPush.*` в глобальной области воркера. Имя намеренно совпадает с
 * page-API: в консоли воркера набирается то же самое, что и на странице.
 */
export function initSwConsoleApi(actions: SwConsoleActions) {
    globalScope.app = {
        webPush: {
            /** Всё состояние воркера: ревизия, конфиг, разрешение, подписка, трей, диплинк. */
            status: () => getSwStatus(actions),
            /**
             * Сбросить клеймы показанных пушей, чтобы повторно отправить тот же `id`, не
             * дожидаясь TTL дедупа (10 минут).
             */
            clearDedup: () => clearShownPushIds(),
        },
    };
}
